import React, { Fragment, useContext, useEffect, useState } from 'react';
// MUI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
// SITE SETTINGS CONTEXT FILE
import { SettingsContext } from 'contexts/settingsContext';
// CUSTOM ICON COMPONENTS
import Menu from 'icons/Menu';
import ThemeIcon from 'icons/ThemeIcon';
// LAYOUT BASED HOOK
import useLayout from 'layouts/layout-1/context/useLayout';
// CUSTOM COMPONENTS
import ProfilePopover from 'layouts/layout-parts/popovers/ProfilePopover';
import LanguagePopover from 'layouts/layout-parts/popovers/LanguagePopover';
import NotificationsPopover from 'layouts/layout-parts/popovers/NotificationsPopover';
// STYLED COMPONENTS
import { DashboardHeaderRoot, StyledToolBar } from 'layouts/layout-1/styles';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { dispatch, useAppSelector } from '../../../store';
import {
  getUserCompaniesAction,
  switchCompanyAction
} from '../../../store/reducers/admin/companyReducer';
import CircularProgress from '@mui/material/CircularProgress';
import {
  getConnectionAction,
  getConnectionActionForHeader
} from '../../../store/reducers/admin/connectionReducer';
import {
  getProvidersAction,
  getProvidersActionForHeader
} from '../../../store/reducers/admin/providerReducer';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import permissionsAccess from '../../../utils/permissionsAccess';
import permissionsList from '../../../constants/permissionsList';
import useAuth from '../../../hooks/useAuth';
import { DashboardActions } from '../../../store/reducers/dashboard/dashboardFiltersReducer';
import useLocation from '../../../hooks/useLocation';
export default function DashboardHeader() {
  const { handleOpenMobileSidebar } = useLayout();
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(1);
  const { settings, saveSettings } = useContext(SettingsContext);
  const upSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const downMd = useMediaQuery((theme) => theme.breakpoints.down(1200));

  const userCompaniesList = useAppSelector(
    (state) => state.company.userCompaniesList
  );
  const currentUserInfo = useAppSelector(
    (state) => state.users.currentUserInfo
  );

  const requestorsList = useAppSelector(
    (state) => state.connections.connectionActionForHeader
  );

  const providersList = useAppSelector(
    (state) => state.providers.providersListForHeader
  );

  const companyId = useAppSelector((state) => state.dashboardFilters.companyId);
  const side = useAppSelector((state) => state.dashboardFilters.side);

  useEffect(() => {
    if (requestorsList?.length > 0 && providersList?.length === 0) {
      dispatch(DashboardActions.setSide(false));
      dispatch(
        DashboardActions.setCompanyId(
          requestorsList.map((item) => item.companyId)
        )
      );
    } else if (providersList?.length) {
      dispatch(DashboardActions.setSide(true));
      dispatch(
        DashboardActions.setCompanyId(
          providersList.map((item) => item.companyId)
        )
      );
    }
  }, [requestorsList, providersList]);


  const { role } = useAuth();

  const canReadProvider = permissionsAccess(
    role,
    permissionsList.ProviderInvitation
  ).read;

  const canReadRequestor = permissionsAccess(
    role,
    permissionsList.Connections
  ).read;

  useEffect(() => {
    if (canReadRequestor) {

      dispatch(
        getConnectionActionForHeader({
          paging: {
            skip: 0,
            take: 100000
          },
          filtering: {
            compositionType: 1,
            memberFilters: [
              {
                name: 'Status',
                comparison: 1,
                value: 2
              }
            ]
          }
        })
      ).catch((error) => console.error('getConnectionActionForHeader failed:', error));
    }
    if (canReadProvider) {

      dispatch(
        getProvidersActionForHeader({
          paging: {
            skip: 0,
            take: 100000
          },
          filtering: {
            compositionType: 1,
            memberFilters: [
              {
                name: 'Status',
                comparison: 1,
                value: 2
              }
            ]
          }
        })
      ).catch((error) => console.error('getProvidersActionForHeader failed:', error));
    }
  }, [canReadRequestor, canReadProvider]);

  const handleChangeDirection = (value) => {
    saveSettings({
      ...settings,
      direction: value
    });
  };
  const handleChangeTheme = (value) => {
    saveSettings({
      ...settings,
      theme: value
    });
  };

  useEffect(() => {
    dispatch(getUserCompaniesAction()).catch((error) => console.error('getUserCompaniesAction failed:', error));
  }, []);

  useEffect(() => {
    if (currentUserInfo) {
      setSelectedCompany(currentUserInfo.companyId);
    }
  }, [currentUserInfo]);

  const handleCompanyChange = async (event) => {
    setLoading(true);
    try {
      const res = await dispatch(switchCompanyAction(event.target.value));
      if (res) {
        window.location.reload();
        setSelectedCompany(event.target.value);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const handleDashboardCompanyChange = (event) => {
    if (event.target.value === '') {
      dispatch(
        DashboardActions.setCompanyId(
          side
            ? providersList.map((item) => item.companyId)
            : requestorsList.map((item) => item.companyId)
        )
      );
    } else {
      dispatch(DashboardActions.setCompanyId(event.target.value));
    }
  };
  const handleSwitch = (event) => {
    dispatch(DashboardActions.setSide(event.target.checked));
    // dispatch(DashboardActions.setCompanyId([]));
    DashboardActions.setCompanyId(
      event.target.checked
        ? providersList.map((item) => item.companyId)
        : requestorsList.map((item) => item.companyId)
    );
  };

  const { pathname } = useLocation();

  const isDashboard = pathname === '/dashboard';


  return (
    <DashboardHeaderRoot position="sticky">
      <StyledToolBar>
        {/* SMALL DEVICE SIDE BAR OPEN BUTTON */}
        {downMd && (
          <IconButton onClick={handleOpenMobileSidebar}>
            <Menu />
          </IconButton>
        )}
        {loading && (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
          </>
        )}
        {userCompaniesList?.length > 1 &&
          !loading &&
          !currentUserInfo?.isExternal && (
            <Select
              value={selectedCompany}
              onChange={handleCompanyChange}
              displayEmpty
            >
              {userCompaniesList.map((company) => (
                <MenuItem key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </MenuItem>
              ))}
            </Select>
          )}
        {isDashboard &&
          providersList?.length > 0 &&
          requestorsList?.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <Typography variant="body2">Requestor</Typography>
              <Switch
                checked={side}
                onChange={handleSwitch}
                name="status"
                color="primary"
                size="small"
              />
              <Typography variant="body2">Provider</Typography>
            </Box>
          )}
        <Box sx={{ ml: 1 }}>
          {requestorsList?.length > 1 && !side && isDashboard && (
            <Select
              size="small"
              value={Array.isArray(companyId) ? '' : companyId}
              onChange={handleDashboardCompanyChange}
              displayEmpty
            >
              <MenuItem value="">Total</MenuItem>
              {requestorsList.map((company) => (
                <MenuItem key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </MenuItem>
              ))}
            </Select>
          )}
          {providersList?.length > 1 && side && isDashboard && (
            <Select
              size="small"
              value={Array.isArray(companyId) ? '' : companyId}
              onChange={handleDashboardCompanyChange}
              displayEmpty
            >
              <MenuItem value="">Total</MenuItem>
              {providersList.map((company) => (
                <MenuItem key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>

        <Box flexGrow={1} ml={1} />

        {/* THEME SWITCH BUTTON */}
        <IconButton
          onClick={() =>
            handleChangeTheme(settings.theme === 'light' ? 'dark' : 'light')
          }
        >
          <ThemeIcon />
        </IconButton>

        {upSm && (
          <Fragment>
            {/*<LanguagePopover />*/}
            <NotificationsPopover />
            {/*<ServicePopover />*/}
          </Fragment>
        )}

        <ProfilePopover />
      </StyledToolBar>
    </DashboardHeaderRoot>
  );
}
